"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { ArrowLeft, Lock, CreditCard, CheckCircle2, AlertCircle } from "lucide-react";

export default function CheckoutPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>("");

  // Check if payment was canceled
  const wasCanceled = searchParams.get('canceled') === 'true';

  useEffect(() => {
    // Optionally get current user info if logged in
    fetch('/api/auth/me')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setUserEmail(data.user.email);
        }
      })
      .catch(err => {
        // Silently fail - guests are allowed
        console.log('Guest checkout - no user session');
      });
  }, []);

  const handleCheckout = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      // Create Stripe Checkout Session
      const response = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to create checkout session');
        setIsProcessing(false);
        return;
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError('Failed to get checkout URL');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setError('An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="container mx-auto px-6 py-6">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-2 text-center">
            Complete Your Purchase
          </h1>
          <p className="text-gray-400 text-center mb-12">
            You're one step away from exclusive access
          </p>

          {/* Canceled Alert */}
          {wasCanceled && (
            <div className="mb-8 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-200 font-medium">Payment Canceled</p>
                <p className="text-yellow-200/70 text-sm mt-1">
                  Your payment was canceled. Click below to try again.
                </p>
              </div>
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <div className="mb-8 bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-200 font-medium">Error</p>
                <p className="text-red-200/70 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Order Summary Card */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Image
                src="/images/mockups/projectlogo.png"
                alt="LuxSupply"
                width={64}
                height={64}
                className="rounded-lg"
              />
              <div>
                <h3 className="text-xl font-bold text-white">
                  LuxSupply Premium Access
                </h3>
                <p className="text-sm text-gray-400">One-time payment • Lifetime access</p>
              </div>
            </div>

            <div className="border-t border-white/10 pt-6 space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-gray-300">
                  Instant access after payment confirmation
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-gray-300">
                  No subscriptions or recurring fees
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-gray-300">
                  Secure card payment via Stripe
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-gray-300">
                  {userEmail
                    ? `Access link sent to ${userEmail}`
                    : 'Access link sent to your email after payment'}
                </span>
              </div>
            </div>

            <div className="border-t border-white/10 mt-8 pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400">Subtotal</span>
                <span className="text-white font-semibold">$35.00</span>
              </div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-400">Processing fee</span>
                <span className="text-white font-semibold">$0.00</span>
              </div>
              <div className="flex items-center justify-between text-xl">
                <span className="text-white font-bold">Total</span>
                <span className="text-primary font-bold">$35.00 USD</span>
              </div>
            </div>
          </div>

          {/* Payment Method Card */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-8">
            <h2 className="text-xl font-bold text-white mb-4">Payment Method</h2>

            <div className="bg-primary/10 border-2 border-primary rounded-xl p-6">
              <div className="flex items-center justify-center gap-3 mb-4">
                <CreditCard className="w-8 h-8 text-primary" />
                <div>
                  <div className="text-lg font-semibold text-white">
                    Secure Checkout
                  </div>
                  <div className="text-sm text-gray-400">
                    Multiple payment options available
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-300 text-center mb-4">
                You'll be redirected to Stripe's secure checkout. Choose from:
              </p>

              <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-gray-400">
                <span className="bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
                  💳 Credit Card
                </span>
                <span className="bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
                  💳 Debit Card
                </span>
                <span className="bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
                  🍎 Apple Pay
                </span>
                <span className="bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
                  💵 Cash App Pay
                </span>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                Payment methods shown based on device compatibility
              </p>
            </div>
          </div>

          {/* Checkout Button */}
          <button
            onClick={handleCheckout}
            disabled={isProcessing}
            className="w-full bg-primary text-black font-bold text-lg py-5 rounded-lg hover:bg-primary/90 transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 mb-4"
          >
            {isProcessing ? (
              <span className="flex items-center justify-center gap-3">
                <svg
                  className="animate-spin h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Redirecting to checkout...
              </span>
            ) : (
              "Pay with Card - $35.00 USD"
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mb-8">
            By completing this purchase, you agree to our{" "}
            <Link
              href="/terms"
              className="text-white hover:text-primary transition-colors"
            >
              Terms of Service
            </Link>
          </p>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-3 text-gray-400 text-sm">
            <Lock className="w-5 h-5" />
            <p>
              Your payment information is encrypted and secure. Powered by Stripe.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
